import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { HasServerResponse } from '../../../components/PanelContainer';
import { Alert } from '../../../components/alert/Alert';
import { Project } from '../../../app/entities/project';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { catchError } from '../../../core/catch-error';

import styles from './delete-project.modal.module.scss';
interface ModalProps {
  onCancel: () => void;
  onSuccess: () => void;
  projectId: string;
}

export const DeleteProjectModal: React.FC<ModalProps> = ({ onCancel, onSuccess, projectId }) => {
  const { t } = useTranslation('deleteProject');
  const project = Project.getOne(projectId);
  const [serverError, setServerError] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const { watch, control, handleSubmit } = useForm({
    defaultValues: {
      name: '',
    },
  });

  const handleOnSubmit = async () => {
    if (isLoading || !project) {
      return;
    }

    setIsLoading(true);

    project
      .delete()
      .then(() => {
        setIsLoading(false);
        onSuccess();
        toastPool.insert(new Toast('Project deleted successfully'));
      })
      .catch((error: any) => {
        setServerError(error?.data?.errors?.[0]?.message);
        catchError(error);
        setIsLoading(false);
      });
  };

  const [nameValue] = watch(['name']);

  return (
    <Dialog
      title={t('delete_project')}
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          type="submit"
          variant="danger"
          text={'Delete this project'}
          key={'delete-project'}
          form="delete-project-form"
          disabled={nameValue !== project?.name}
          isLoading={isLoading}
        />,
      ]}
    >
      {serverError && (
        <HasServerResponse className={styles['server-error-message']}>
          <Alert text={serverError} />
        </HasServerResponse>
      )}

      <p className={styles['description']}>
        {t('delete_project_description', { name: project?.name })}
      </p>

      <form id="delete-project-form" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className={styles['input-container']}>
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value, name, ref }, fieldState }) => (
              <TextInput
                ref={ref}
                value={value}
                name={name}
                onChange={onChange}
                label={t('project_name_label', { name: project?.name })}
                placeholder={project?.name}
                errorMessage={fieldState.error?.message}
                required
              />
            )}
          />
        </div>

        {serverError && serverError.response && (
          <HasServerResponse>
            <Alert text={serverError.response.data.message} />
          </HasServerResponse>
        )}
      </form>
    </Dialog>
  );
};
